import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import { notificationsApi } from "../api";
import type { NotificationCategory, NotificationChannel, NotificationPreferences } from "../types";
import { phoneError, toE164 } from "../utils/phone";

const CATEGORIES: { key: NotificationCategory; label: string }[] = [
  { key: "email_digest", label: "Email digest" },
  { key: "event_alerts", label: "Event alerts" },
  { key: "marketing", label: "Product updates" },
];

const CONSENT_TEXT = "I agree to receive Zoniq notifications on WhatsApp at this number. I can turn this off any time.";

type Draft = NotificationPreferences["channels"];

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/** Per-category email / WhatsApp switches, plus verifying and revoking a WhatsApp number. */
export const NotificationPreferencesCard: React.FC<{ token: string }> = ({ token }) => {
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const apply = useCallback((next: NotificationPreferences) => {
    setPrefs(next);
    setDraft(next.channels);
  }, []);

  useEffect(() => {
    let cancelled = false;
    notificationsApi
      .getPreferences(token)
      .then((next) => {
        if (!cancelled) apply(next);
      })
      .catch(() => {
        // Preferences are non-critical; show the card empty rather than blocking Profile.
      });
    return () => {
      cancelled = true;
    };
  }, [token, apply]);

  if (!prefs || !draft) return null;

  const whatsappAvailable = prefs.available_channels.whatsapp;
  const verified = prefs.whatsapp.verified;

  const setSwitch = (category: NotificationCategory, channel: NotificationChannel, value: boolean) =>
    setDraft((d) => (d ? { ...d, [category]: { ...d[category], [channel]: value } } : d));

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) {
      setEditing(true);
      return;
    }
    setSaving(true);
    try {
      apply(await notificationsApi.updatePreferences({ channels: draft }, token));
      setEditing(false);
      toast.success("Notification preferences saved");
    } catch (err) {
      toast.error(errorMessage(err, "Could not save preferences"));
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft(prefs.channels);
    setEditing(false);
  };

  const withBusy = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const sendCode = () =>
    withBusy(async () => {
      try {
        const canonical = toE164(phone);
        if (!canonical) {
          toast.error(phoneError(phone) ?? "Enter a valid mobile number");
          return;
        }
        apply(await notificationsApi.requestWhatsAppCode(canonical, token));
        toast.success("Code sent on WhatsApp");
      } catch (err) {
        toast.error(errorMessage(err, "Could not send the code"));
      }
    });

  const verifyCode = () =>
    withBusy(async () => {
      try {
        apply(await notificationsApi.verifyWhatsAppCode(code.trim(), CONSENT_TEXT, token));
        setPhone("");
        setCode("");
        setConsent(false);
        toast.success("WhatsApp number verified");
      } catch (err) {
        toast.error(errorMessage(err, "Could not verify the code"));
      }
    });

  const remove = () =>
    withBusy(async () => {
      try {
        apply(await notificationsApi.revokeWhatsApp(token));
        toast.success("WhatsApp notifications turned off");
      } catch (err) {
        toast.error(errorMessage(err, "Could not remove the number"));
      }
    });

  return (
    <form
      onSubmit={save}
      className="eventos-form eventos-account-form-inner eventos-account-section eventos-account-section--profile-card"
    >
      <div className="eventos-label">Notifications</div>

      <table className="eventos-table eventos-table--fit" aria-label="Notification channels">
        <thead>
          <tr>
            <th />
            <th>Email</th>
            <th>WhatsApp</th>
          </tr>
        </thead>
        <tbody>
          {CATEGORIES.map(({ key, label }) => (
            <tr key={key}>
              <td>{label}</td>
              {(["email", "whatsapp"] as NotificationChannel[]).map((channel) => {
                const isWhatsApp = channel === "whatsapp";
                const locked = isWhatsApp && (!whatsappAvailable || !verified);
                return (
                  <td key={channel}>
                    <input
                      type="checkbox"
                      aria-label={`${label} by ${channel}`}
                      checked={draft[key][channel]}
                      disabled={!editing || locked}
                      onChange={(e) => setSwitch(key, channel, e.target.checked)}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {!whatsappAvailable ? (
        <p className="eventos-subtle">WhatsApp notifications are not available yet.</p>
      ) : verified ? (
        <p className="eventos-subtle">
          WhatsApp verified: {prefs.whatsapp.number}{" "}
          <button type="button" className="eventos-nav-btn" onClick={() => void remove()} disabled={busy}>
            Remove number
          </button>
        </p>
      ) : (
        <div className="eventos-field">
          <span className="eventos-field-label">WhatsApp number</span>
          <input
            className="eventos-input"
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+91 98765 43210"
            autoComplete="tel"
          />
          <label className="eventos-check">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>{CONSENT_TEXT}</span>
          </label>
          <button
            type="button"
            className="eventos-nav-btn"
            disabled={busy || !phone.trim() || !consent}
            onClick={() => void sendCode()}
          >
            {prefs.whatsapp.verification_pending ? "Resend code" : "Send code"}
          </button>
          {prefs.whatsapp.verification_pending ? (
            <>
              <input
                className="eventos-input"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="6-digit code"
                aria-label="WhatsApp verification code"
              />
              <button
                type="button"
                className="eventos-nav-btn"
                disabled={busy || code.length !== 6}
                onClick={() => void verifyCode()}
              >
                Verify
              </button>
            </>
          ) : null}
        </div>
      )}

      <button className="eventos-nav-btn eventos-account-section-btn eventos-profile-card-btn" type="submit" disabled={saving}>
        {saving ? "Saving…" : editing ? "Save" : "Edit"}
      </button>
      {editing ? (
        <button type="button" className="eventos-nav-btn" onClick={cancel} disabled={saving}>
          Cancel
        </button>
      ) : null}
    </form>
  );
};
